/**
 * Setup candidate labelling (research tool)
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ THERE IS NO SCORER IN THIS FILE, AND THERE MUST NEVER BE ONE AGAIN.     │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * `utt-engine/src/strategy/setup_candidate.rs` is the scorer. It is what the
 * bot trades. This file used to hold a COPY of that rule, and the copy drifted:
 * a bar the bot shorted on a logged `score=63 grade=strong` rendered on the
 * chart as 53 / moderate. Every constant, ramp, dock and condition that made
 * that possible has been deleted.
 *
 * The copy survived as long as it did for one reason: `diamond-signal` is
 * live-only, so on a fresh page load no historical bar had an engine score.
 * `GET /api/diamonds/history` closed that, and it returns objects byte-identical
 * to the socket payload — same struct, same `with_symbol` helper, same
 * `detect_candidates` call. So there is now exactly one source of a diamond's
 * score, and this module never computes one.
 *
 * What lives here now:
 *   - the WIRE SHAPES of what the engine sends (`EngineDiamond`,
 *     `EngineRejectedBar`, `DiamondHistoryResponse`)
 *   - the LABELLING half: verdicts, presets, review sets, partitioning,
 *     auto-tagging, descriptions. Human review data, not scoring.
 *
 * If you are about to add a function here that takes a candle and returns a
 * number a user will see as a score or a grade, stop. That is the bug.
 */

// ============================================================================
// What the engine sends
// ============================================================================

/**
 * The engine's spelling. Rust emits "long"/"short" on `SetupCandidate.direction`
 * and this side used to say "bull"/"bear" — two vocabularies for one fact, and
 * a translation layer is somewhere for them to come apart. The engine's wins.
 */
export type CandidateDirection = 'long' | 'short';

/**
 * One diamond exactly as the engine scored it.
 *
 * Field names are the camelCase serde spelling of Rust's `SetupCandidate`
 * (`utt-engine/src/strategy/setup_candidate.rs`), plus the `symbol` that
 * `with_symbol` tacks on at the wire. Identical whether it arrived on the
 * `diamond-signal` socket event or in `GET /api/diamonds/history` — that
 * identity is the whole reason the endpoint exists.
 *
 * Everything past `grade` is optional so an engine older than the breakdown
 * fields still parses: a diamond that arrives without a breakdown must still
 * colour its bar rather than fail and take the marker off the chart.
 */
export interface EngineDiamond {
  symbol: string;
  timestamp: number;
  direction: CandidateDirection;
  /** The bar's close — where a trade here would have entered. */
  entry: number;
  score: number;
  /** "super-strong" | "strong" | "above-moderate" | "moderate" | "weak" */
  grade: string;

  // ── The measurements the score was taken from ─────────────────────────────
  parentRange: number;
  parentBodyPct: number;
  childBodyPctOfParentBody: number;
  atrRatio: number;
  /** The tapered HOD/LOD penalty, at face value. */
  hodLodPenalty: number;

  // ── How `score` was built ─────────────────────────────────────────────────
  //
  //     v2ChildPoints + v2ParentPoints + v2AtrPoints
  //       - v2HodLodDockApplied - v2ConsecutiveDockApplied   ==   score
  //
  // (floored at zero, which only bites when the docks exceed the quality).
  // READ, never recomputed — see `readV2Breakdown`. The engine pins this exact
  // identity in its own tests; re-deriving it here would be the second
  // implementation all over again.
  //
  // `v2ConsecutiveDockApplied` was removed once, when the engine dropped the
  // consecutive rule, and is back now that the rule works on both paths. It is
  // read as `?? 0` rather than required — see `readV2Breakdown` for why that is
  // correct rather than lax.
  v2ChildPoints?: number;
  v2ParentPoints?: number;
  v2AtrPoints?: number;
  v2HodLodDockApplied?: number;
  v2ConsecutiveDockApplied?: number;

  /**
   * The run this signal fired into — engine-computed, see `trend_context.rs`.
   *
   * CONTEXT, not a scoring term. Nothing in `score` is derived from it, and the
   * overlay must not imply otherwise: measured over 772 diamonds, the
   * counter-trend label predicted nothing at any lookback from 10 to 60 minutes.
   * It is here because a trader can use information the scorer cannot.
   *
   * Absent on bars with less than a full lookback of history behind them.
   */
  trend?: TrendContext;

  /**
   * What this signal WOULD have done if taken — engine-computed, forward
   * looking. Absent on bars whose outcome cannot be resolved from the loaded
   * candles.
   *
   * Deliberately not part of `score`: the scorer judges a bar from the bars
   * before it, and feeding it the outcome is how a rule quietly becomes a
   * fitted model.
   */
  simulated?: SimulatedTrade;

  /**
   * Where this signal sat against the EMAs and session VWAP — engine-computed,
   * see `trend_alignment.rs`.
   *
   * A SHADOW FILTER, and the overlay must render it as one. `wouldSkip` is a
   * verdict nothing acts on — the bot took the signal regardless. Showing it
   * unqualified would read as "the bot skipped this", which is false.
   *
   * Distinct from `trend`, which reads the RUN the signal was born into; this
   * reads position against the standing lines. Both are context, neither feeds
   * `score`.
   *
   * Why only recorded: over 1,803 trades across 34 days, split-half validated,
   * alignment leaned the right way in both halves — but the best of 21 tested
   * buckets reached +1.73 SE against the +2.5 SE that testing 21 buckets
   * demands. It has to prove itself forward.
   *
   * Absent on bars predating the indicator series the engine retains.
   */
  trendAlignment?: TrendAlignment;
}

/**
 * A signal's position relative to the trend reference lines.
 *
 * Carries the inputs alongside the verdict, not just the booleans, so a
 * DIFFERENT rule can be scored from recorded signals later without re-deriving
 * indicators.
 */
export interface TrendAlignment {
  ema9: number;
  ema21: number;
  ema200: number;
  vwap: number;
  /** Price on the trade's own side of the 200 EMA. */
  withEma200: boolean;
  /** Price on the trade's own side of session VWAP. */
  withVwap: boolean;
  /** 9 over 21 for a long, under for a short. The 200 is not consulted. */
  stackAligned: boolean;
  /**
   * How many of the three EMAs cut the signal candle's BODY.
   *
   * The proposed rule was to skip at 2 or more. It failed validation: the
   * signals it would have dropped were 4.3pp BETTER in-sample and 2.7pp worse
   * out-of-sample — a sign flip inside one month. Shown because it was asked
   * for and only a longer sample settles it, but it carries no verdict.
   */
  emasThroughBody: number;
  /**
   * THE RULE UNDER TEST: skip when the short-term RUN disagrees with the
   * signal, or when 2+ EMAs cut the signal candle's body. NOTHING acts on it.
   *
   * Chosen over the EMA200/VWAP version after the two disagreed on
   * 2026-08-13 11:06 — a short 7.3 ATR above VWAP that the standing lines
   * skipped, the run took, and which won.
   */
  wouldSkip: boolean;
  /**
   * The rule's halves, plus a rival reading of trend. None of them act.
   *
   * Kept separate because an OR fires for two different reasons and the
   * combined verdict cannot say which — the forward sample needs to see
   * whether the run half carries it and the body half merely dilutes.
   */
  variants: SkipVariants;
}

/** Each half of the rule, plus a rival trend reading. Nothing reads these. */
export interface SkipVariants {
  /**
   * The signal fights the short-term run — net displacement over the 10 bars
   * BEFORE it, sign only. No quality bar: ten choppy bars closing 0.25 lower
   * read "down" as strongly as a clean 46-point slide.
   */
  counterRun: boolean;
  /** 2 or more of the three EMAs cut the signal candle's body. */
  throughBody: boolean;
  /**
   * The EMA STACK explicitly runs against the trade (9 under 21 for a long).
   * The 200 is not consulted. A second, faster reading of trend, recorded
   * because it and `counterRun` measure different things and can disagree.
   *
   * With two lines there is no middle ground except an exact tie, so this
   * fires on roughly every signal the pair does not favour — much broader
   * than the three-line version it replaced.
   */
  stackOpposed: boolean;
}

/**
 * How a simulated trade ended.
 *
 * `unfilled` is NOT a flavour of loss: the taking bot rests its entry and price
 * never returned to the limit before it expired, so there was no trade at all.
 * Counting those alongside wins is the bug this union exists to prevent — and
 * they skew one way, since with a small target a signal that never retraces is
 * usually one that ran straight to it.
 *
 * `ambiguous` is a bar that touched BOTH levels, so which came first is not
 * recoverable from OHLC. It used to resolve as a loss, which was near-free at a
 * 111-point bracket and a systematic bias at a 17-point one. Excluded from the
 * win rate rather than guessed at.
 */
export type SimulatedResult = 'win' | 'loss' | 'open' | 'unfilled' | 'ambiguous' | 'flat';

/** Mirrors `SimulatedTrade` in utt-engine/src/main.rs. */
export interface SimulatedTrade {
  result: SimulatedResult;
  /** Signed points: +TP on a win, -SL on a loss, 0 while open. */
  points: number;
  /** Points at the taking bot's size, or one contract when unattributed. */
  dollars: number;
  /** Where it entered — bar close, or the bot's resting limit if it uses one. */
  entry: number;
  tpPoints: number;
  slPoints: number;
  /**
   * Whose band this signal falls in, or null.
   *
   * Band and direction only. Time windows, blackouts, cooldowns and position
   * state are NOT replayed — they depend on the state of the world at that
   * instant, which is not recoverable afterwards. Read it as "in botA's band",
   * not "botA would certainly have traded it".
   */
  botName: string | null;
  quantity: number;
  /**
   * When the trade resolved, or absent while still open.
   *
   * What lets a scoreboard answer "was the account free to take the next
   * signal". Without it every simulated trade looks instantaneous.
   */
  exitTimestamp?: number | null;
  /**
   * Worst point price reached AGAINST the position while it was open, signed
   * negative. `null` when there was no held trade to measure.
   *
   * Null rather than zero on purpose: zero reads as "no heat taken", which is
   * a claim about a trade that never happened. An `unfilled` signal never had
   * a position at all.
   */
  mae?: number | null;
  /**
   * Best point price reached IN FAVOUR while the position was open, signed
   * positive. Null on the same terms as {@link SimulatedTrade.mae}.
   *
   * Excursion, not realised P&L: on a win this is what the market did, not the
   * `tpPoints` the bracket took, so it routinely exceeds the target. Pinning
   * it to the target would make the row a constant.
   */
  mfe?: number | null;
  /** {@link SimulatedTrade.mae} clipped to the stop this trade ran under. */
  maeCapped?: number | null;
  /**
   * {@link SimulatedTrade.mfe} clipped to the target this trade ran under.
   *
   * Displayed as `capped (true)`: `6.0 (9.25)` banked six while the market went
   * on to offer nine and a quarter. Equal values mean the move never reached
   * the target, so nothing was left behind.
   */
  mfeCapped?: number | null;
}

/** Mirrors `TrendContext` in utt-engine/src/strategy/trend_context.rs. */
export interface TrendContext {
  /** Bars the run was measured over. */
  runBars: number;
  /** Net points across the run, signed. Negative is a downtrend. */
  runPoints: number;
  /**
   * Share of bars agreeing with the run's direction, 0-1. What separates a
   * slide from chop that happened to end lower: -46 points at 0.8 is a run,
   * the same -46 at 0.5 is noise.
   */
  agree: number;
  /**
   * Goodness of fit of a line through the run's closes, 0-1.
   *
   * Sits beside `agree` rather than replacing it because they disagree in the
   * case that matters: nine bars drifting a tick lower and one collapsing 40
   * points is 100% agreeing and a poor line fit. `agree` counts how many bars
   * pulled the same way, `r2` says whether the path was actually a line.
   *
   * Zero when the run is flat — a flat line fits flat data perfectly, but that
   * is dead air, not a trend.
   */
  r2: number;
  /** True when the signal points against the run. */
  counterTrend: boolean;
  /**
   * Share of the run's points the signal bar's body took back. Uncapped — 1.0
   * means it undid the whole leg in one bar.
   */
  recovered: number;
  /** Did it close past the opens of the last two bars of the run. */
  closedBeyond: boolean;
  /**
   * How many consecutive preceding bars the signal reached past — highs cleared
   * for a long, lows undercut for a short — out of `runBars`.
   *
   * The structural reading of displacement, and the one that matches how the
   * setup gets described out loud: "it swept the highs of the last couple of
   * candles". Scale-free, so it needs no reference to points or to an average
   * that may be smoothed over the wrong stretch.
   *
   * Contiguous: it stops at the first bar the signal failed to clear, rather
   * than counting every lower bar in the window. A high that blocked the move
   * blocks it regardless of what sits behind it.
   */
  sweptBars: number;
}

/**
 * A bar the engine judged and REJECTED, as returned by
 * `/api/diamonds/history?includeRejected=true`.
 *
 * Metrics are nullable because a bar can fail before there is anything to
 * measure: a candle with no trailing bar has no child body, and reporting one
 * as 0 would read as "a perfectly tight child" — the exact inversion the
 * `childBodyMinPctOfParentBody` floor exists to stop.
 *
 * This is what makes a hand-labelled `missed` bar resurface with its numbers
 * instead of vanishing. Without it the only way to explain a rejection was to
 * re-run the conditions locally, which is the thing being deleted.
 */
export interface EngineRejectedBar {
  symbol: string;
  timestamp: number;
  /** `RejectReason` in camelCase: "noTrailingBar", "childBodyTooLarge", … */
  reason: string;
  /** Human-readable, carrying the number that lost and the threshold it lost to. */
  detail: string;
  entry: number;
  direction?: CandidateDirection | null;
  parentRange?: number | null;
  parentBodyPct?: number | null;
  childBodyPctOfParentBody?: number | null;
  atrRatio?: number | null;
}

/**
 * `GET /api/diamonds/history?symbol=&days=&includeRejected=` in full.
 *
 * The coverage counters are not decoration. `days < daysRequested` says the
 * loaded history ran out, and `candlesInWindow - candlesJudged` is the blind
 * spot: bars inside the window that had no trailing data, so nobody looked at
 * them. With no local scorer left, "no diamond here" and "nobody looked here"
 * are different answers and the chart has to be able to tell them apart.
 */
export interface DiamondHistoryResponse {
  symbol: string;
  /** Sessions actually served. */
  days: number;
  daysRequested: number;
  /** Start of the oldest session served, or null when nothing was loaded. */
  fromMs: number | null;
  candlesLoaded: number;
  candlesInWindow: number;
  candlesJudged: number;
  includeRejected: boolean;
  diamonds: EngineDiamond[];
  rejected?: EngineRejectedBar[];
}

/** Stable id for a bar, shared by labels, markers and the engine-diamond map. */
export const candidateId = (symbol: string, timestamp: number): string =>
  `${symbol}:${timestamp}`;

/**
 * The rule, as the ENGINE holds it.
 *
 * Kept as a type only — there is deliberately no local default object. The
 * previous one shipped `hodLodRangePoints: 50 / penalty: 25` against the
 * engine's 15 / 20, so a browser that had not yet heard back from
 * `/api/diamond-settings` drew a rule nothing was trading. `thresholds` is null
 * until the engine answers, and the panel says so.
 *
 * These are displayed and edited (the panel POSTs them back), never used to
 * score anything on this side.
 */
export interface CandidateThresholds {
  /** Child body must be at most this % of the PARENT'S BODY. */
  childBodyMaxPctOfParentBody: number;
  /** Child body must be at least this % of the parent's body. */
  childBodyMinPctOfParentBody: number;
  /** Parent body must be at least this % of the parent's range. */
  parentBodyMinPct: number;
  /** Require the trailing bar to point the same way as the parent. */
  requireDirectionAgree: boolean;
  useAtrFilter: boolean;
  atrMult: number;
  atrPeriod: number;
  /**
   * Absolute ATR floor in POINTS, below which a bar is not evaluated at all.
   *
   * Not the same question as `atrMult`, which is a RATIO: that asks whether a
   * bar is big relative to recent range, this asks whether there is enough
   * range to be worth trading. A quiet tape passes the ratio easily — when
   * everything is small, a slightly-less-small bar still clears it. 0 disables.
   */
  minAtr: number;
  /** Penalise setups near the session high/low. */
  hodLodEnabled: boolean;
  hodLodRangePoints: number;
  hodLodPenalty: number;
  superStrongScore: number;
  strongScore: number;
  /** The slice between moderate and strong — the bot's threshold sits mid-band. */
  aboveModerateScore: number;
  moderateScore: number;
}

// ============================================================================
// The score breakdown, read back
// ============================================================================

/**
 * Anything carrying the engine's four v2 components and the score they made.
 *
 * `v2ConsecutiveDockApplied` was a fifth. The engine no longer sends it, and it
 * is deliberately absent here rather than kept as a deprecated optional: an
 * extra key on an inbound payload is ignored at runtime, so a stale message
 * costs nothing, whereas a declared field invites something to start reading it
 * again.
 */
export interface V2Carrier {
  score?: number | null;
  v2ChildPoints?: number;
  v2ParentPoints?: number;
  v2AtrPoints?: number;
  v2HodLodDockApplied?: number;
  v2ConsecutiveDockApplied?: number;
}

/** The five components plus the score they belong to. */
export interface V2ScoreParts {
  childPoints: number;
  parentPoints: number;
  atrPoints: number;
  hodLodDock: number;
  /** Points removed for following other candidates. See `readV2Breakdown`. */
  consecutiveDock: number;
  /**
   * The engine's `score`, verbatim.
   *
   * Deliberately NOT the sum of the four rows above. It used to be — the popup
   * re-added the parts and clamped them, which is a re-implementation of the
   * scorer's last line and free to disagree with the number it sits under. The
   * engine already pins the identity in its own tests; the display's job is to
   * print what arrived.
   */
  total: number;
}

/**
 * Read a diamond's breakdown back off it, for display.
 *
 * A READ, never a recomputation. Returns null when the carrier predates the
 * component fields or has no score (a rejected bar), where showing a partial
 * set would read as a complete one.
 *
 * The validity check lists exactly the fields the engine still sends. It used
 * to include `v2ConsecutiveDockApplied`, which meant that the moment the engine
 * stopped emitting that key EVERY diamond would fail this check and the
 * breakdown popup would go blank — a required-field check on a field nobody
 * required. Anything a payload carries beyond these is ignored.
 *
 * `v2ConsecutiveDockApplied` is REQUIRED-BY-DEFAULT rather than required: it is
 * read as `?? 0` and stays out of the check above. That is not laxness, it is
 * the correct value for the payloads that lack it. A diamond emitted by an
 * engine from before the consecutive dock existed had no such dock applied, so
 * its parts genuinely sum to its score with a zero in that slot — and during a
 * rolling deploy those payloads are in flight and in cache. Requiring the key
 * would blank the popup for exactly as long as the old engine kept talking.
 */
export function readV2Breakdown(c: V2Carrier): V2ScoreParts | null {
  const parts = [
    c.v2ChildPoints,
    c.v2ParentPoints,
    c.v2AtrPoints,
    c.v2HodLodDockApplied,
    c.score,
  ];
  // All or nothing: a half breakdown still adds up to something, and that
  // something is wrong.
  if (parts.some((v) => typeof v !== 'number' || !Number.isFinite(v))) return null;
  const consecutiveDock = c.v2ConsecutiveDockApplied;
  return {
    childPoints: c.v2ChildPoints!,
    parentPoints: c.v2ParentPoints!,
    atrPoints: c.v2AtrPoints!,
    hodLodDock: c.v2HodLodDockApplied!,
    consecutiveDock:
      typeof consecutiveDock === 'number' && Number.isFinite(consecutiveDock)
        ? consecutiveDock
        : 0,
    total: c.score!,
  };
}

// ============================================================================
// Labelling
// ============================================================================

/**
 * One bar in the review list.
 *
 * Built ONLY from what the engine sent — an accepted diamond or, for a bar you
 * hand-labelled `missed`, a rejected one. Nothing here is derived from candles
 * on this side, which is what stops a second opinion existing at all.
 *
 * `score` and `grade` are null on a rejected bar because the engine did not
 * score it. That is not a gap to fill in: a bar the rule threw out has no
 * score, and inventing one is exactly how the chart came to disagree with the
 * bot. The marker for it draws without a number.
 */
export interface ReviewCandidate extends V2Carrier {
  /** `${symbol}:${timestamp}`. */
  id: string;
  symbol: string;
  timestamp: number;
  /** Null only on an availability rejection, where the bar was never examined. */
  direction: CandidateDirection | null;
  entry: number;
  score: number | null;
  grade: string | null;
  parentRange: number | null;
  parentBodyPct: number | null;
  childBodyPctOfParentBody: number | null;
  atrRatio: number | null;
  hodLodPenalty: number | null;
  /**
   * The run this bar fired into. Context only — never part of `score`.
   * Absent on manual rows and on bars without a full lookback behind them.
   */
  trend?: TrendContext;
  /**
   * Where this bar sat against the EMAs and VWAP. Context only — never part of
   * `score`. A SECOND reading of trend beside `trend`, which signs the run: the
   * two measure different things and can disagree, which is why the chart shows
   * both. Absent on bars predating the indicator series the engine retains.
   */
  trendAlignment?: TrendAlignment;
  /** What it would have done if taken. Context only — never part of `score`. */
  simulated?: SimulatedTrade;
  /** Why the engine threw this bar out. Null on an accepted diamond. */
  rejection: { reason: string; detail: string } | null;
  /**
   * How this bar got onto the chart:
   * - "detected": the engine's rule fired
   * - "manual":   the rule rejected it and you marked it as one it MISSED
   */
  source: CandidateSource;
}

export type CandidateSource = 'detected' | 'manual';

/** An accepted engine diamond, as a review row. */
export function fromEngineDiamond(d: EngineDiamond): ReviewCandidate {
  return {
    id: candidateId(d.symbol, d.timestamp),
    symbol: d.symbol,
    timestamp: d.timestamp,
    direction: d.direction,
    entry: d.entry,
    score: d.score,
    grade: d.grade,
    parentRange: d.parentRange,
    parentBodyPct: d.parentBodyPct,
    childBodyPctOfParentBody: d.childBodyPctOfParentBody,
    atrRatio: d.atrRatio,
    hodLodPenalty: d.hodLodPenalty,
    v2ChildPoints: d.v2ChildPoints,
    v2ParentPoints: d.v2ParentPoints,
    v2AtrPoints: d.v2AtrPoints,
    v2HodLodDockApplied: d.v2HodLodDockApplied,
    v2ConsecutiveDockApplied: d.v2ConsecutiveDockApplied,
    trend: d.trend,
    trendAlignment: d.trendAlignment,
    simulated: d.simulated,
    rejection: null,
    source: 'detected',
  };
}

/** A rejected bar, as a review row. Carries no score — the engine gave it none. */
export function fromEngineRejection(r: EngineRejectedBar): ReviewCandidate {
  return {
    id: candidateId(r.symbol, r.timestamp),
    symbol: r.symbol,
    timestamp: r.timestamp,
    direction: r.direction ?? null,
    entry: r.entry,
    score: null,
    grade: null,
    parentRange: r.parentRange ?? null,
    parentBodyPct: r.parentBodyPct ?? null,
    childBodyPctOfParentBody: r.childBodyPctOfParentBody ?? null,
    atrRatio: r.atrRatio ?? null,
    hodLodPenalty: null,
    rejection: { reason: r.reason, detail: r.detail },
    source: 'manual',
  };
}

/**
 * Strength grades for a candidate you accept, plus the two rejection states.
 *
 * - `strong` / `moderate` / `weak` — it IS a setup, graded by quality
 * - `not-ideal` — not a setup at all, with a reason
 * - `missed`    — the rule did NOT fire but should have; you added it by hand
 *
 * `ideal` is the legacy name for `strong` and is still accepted when reading
 * old labels — see `normaliseVerdict`.
 */
export type CandidateVerdict =
  | 'super-strong'
  | 'strong'
  | 'moderate'
  | 'weak'
  | 'not-ideal'
  | 'missed';

/**
 * The label vocabulary has no "above-moderate": that band exists to make the
 * bot's mid-band threshold visible on the chart, not to change what you can
 * call a setup. Adding it would alter the training target and re-interpret
 * every label already recorded.
 *
 * Takes the engine's `grade` string, which is why it is defensive: an engine
 * grade this side has never heard of falls to `weak` rather than producing a
 * verdict that is not in the vocabulary.
 */
export function verdictForGrade(grade: string | null | undefined): CandidateVerdict {
  switch (grade) {
    case 'super-strong':
      return 'super-strong';
    case 'strong':
      return 'strong';
    case 'above-moderate':
    case 'moderate':
      return 'moderate';
    default:
      return 'weak';
  }
}

/** Grades that count as "this is a setup", best first. */
export const GRADES: CandidateVerdict[] = ['super-strong', 'strong', 'moderate', 'weak'];
export const ACCEPTED_VERDICTS = GRADES;

/** Map any stored verdict onto the current vocabulary. */
export function normaliseVerdict(v: string): CandidateVerdict {
  if (v === 'ideal') return 'strong';
  if (
    v === 'super-strong' || v === 'strong' || v === 'moderate' ||
    v === 'weak' || v === 'missed'
  ) return v;
  return 'not-ideal';
}

export interface CandidateLabel {
  verdict: CandidateVerdict;
  /** Why. Free text, or one of the presets below. Optional for `ideal`. */
  reason: string;
  /** When it was labelled, ms epoch. */
  at: number;
}

/** Labels keyed by candidate id ("{symbol}:{timestamp}"). */
export type CandidateLabelMap = Record<string, CandidateLabel>;

/**
 * The review list: every bar the ENGINE scored, plus any bar the engine
 * REJECTED that you marked as missed.
 *
 * Both arrays come off the engine — `diamonds` from the socket accumulator and
 * `/api/diamonds/history`, `rejected` from that endpoint with
 * `includeRejected=true`. A `missed` label points at a bar the rule threw out,
 * so its numbers have to come from the rejected set; that is the only way to
 * see WHICH condition wrongly rejected it, and the alternative — re-running the
 * conditions here — is the mirror that drifted.
 *
 * Rejected bars are included ONLY when hand-labelled `missed`. The endpoint
 * returns thousands of them (most for `noTrailingBar`) and they are not review
 * material until you say one is.
 *
 * A bar present in both arrays cannot happen — the engine's `BarOutcome` is one
 * or the other — but if it ever did, the accepted diamond wins: it is the
 * object the bot was handed. Ids are unique in the output either way.
 */
export function buildReviewSet(
  diamonds: EngineDiamond[],
  rejected: EngineRejectedBar[],
  labels: CandidateLabelMap
): ReviewCandidate[] {
  const out: ReviewCandidate[] = [];
  const seen = new Set<string>();

  for (const d of diamonds) {
    const row = fromEngineDiamond(d);
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }

  for (const r of rejected) {
    const id = candidateId(r.symbol, r.timestamp);
    if (seen.has(id)) continue;
    const l = labels[id];
    if (!l || normaliseVerdict(l.verdict) !== 'missed') continue;
    seen.add(id);
    out.push(fromEngineRejection(r));
  }

  return out.sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Starting vocabulary, so labelling works on a fresh browser.
 *
 * These used to be absent by design — the argument being that a canned list
 * pushes you toward the author's categories. In practice it meant an empty row
 * and a text box on every diamond, which is a worse tax than a nudge: you
 * either type the same phrase repeatedly or skip the reason and teach nothing.
 *
 * Your own wording still outranks these (see `collectReasonPresets`), so the
 * moment you type something twice it sits above the canned set.
 */
export const DEFAULT_REASON_PRESETS: readonly string[] = [
  'clean structure',
  'tight child',
  'strong close',
  'big parent body',
  'expansion bar',
  'at hod/lod',
  'choppy context',
  'thin body',
  'weak close',
  'against trend',
  'too extended',
  'quiet range',
];

/**
 * Reason presets: yours first, then the defaults you haven't used.
 *
 * Most-used first, ties broken by most recently used, so the vocabulary you
 * settle into rises to the top and the canned entries fall away as you build
 * your own.
 */
export function collectReasonPresets(labels: CandidateLabelMap, limit = 14): string[] {
  const stats = new Map<string, { count: number; last: number }>();
  for (const l of Object.values(labels)) {
    const reason = (l.reason ?? '').trim();
    if (!reason) continue;
    const prev = stats.get(reason);
    stats.set(reason, {
      count: (prev?.count ?? 0) + 1,
      last: Math.max(prev?.last ?? 0, l.at ?? 0),
    });
  }
  const mine = [...stats.entries()]
    .sort((a, b) => b[1].count - a[1].count || b[1].last - a[1].last)
    .map(([reason]) => reason);

  // Case-insensitive dedupe: "tight child" typed by hand must not sit next to
  // the identical preset just because one of them is capitalised.
  const seen = new Set(mine.map((r) => r.toLowerCase()));
  const filler = DEFAULT_REASON_PRESETS.filter((p) => !seen.has(p.toLowerCase()));

  return [...mine, ...filler].slice(0, limit);
}

/**
 * Plain-language account of what this bar IS, for the reason line.
 *
 * Replaces "matches your strong call at 10:32", which named a different bar and
 * a clock time — neither of which says anything about the one in front of you.
 * A reason has to survive being read back weeks later, when the neighbour it
 * copied from is long out of context.
 *
 * Traits are ranked by how far from unremarkable they are, so the line leads
 * with whatever actually characterises the bar rather than a fixed order.
 *
 * Every trait reads a field the ENGINE sent. It used to also describe close
 * strength and whether the trailing bar took the parent's extreme; neither is
 * on the wire, and re-deriving them from the candle array would put candle
 * arithmetic back in this module for the sake of two adjectives. A bar the
 * engine rejected before measuring anything has nothing to describe and says
 * so, rather than printing zeroes as though they were measurements.
 */
export function describeCandidate(c: ReviewCandidate, limit = 3): string {
  const traits: { salience: number; text: string }[] = [];

  if (c.childBodyPctOfParentBody !== null) {
    const v = c.childBodyPctOfParentBody;
    traits.push(
      v <= 50
        ? { salience: 50 - v, text: `tight child ${v.toFixed(0)}%` }
        : { salience: v - 50, text: `loose child ${v.toFixed(0)}%` }
    );
  }

  if (c.parentBodyPct !== null) {
    const v = c.parentBodyPct;
    traits.push(
      v >= 50
        ? { salience: v - 50, text: `solid body ${v.toFixed(0)}%` }
        : { salience: 50 - v, text: `thin body ${v.toFixed(0)}%` }
    );
  }

  // 1.0x ATR is an unremarkable bar; distance either way is the story.
  if (c.atrRatio !== null) {
    traits.push({
      salience: Math.abs(c.atrRatio - 1) * 40,
      text: `${c.atrRatio.toFixed(2)}x atr`,
    });
  }

  // A dock is never incidental — it is the single thing most likely to explain
  // a score that looks too low for the bar. Both are listed separately rather
  // than summed: "docked 25" tells you the number, "docked 25 for following two
  // signals" tells you what to do about it.
  const dock = c.v2HodLodDockApplied ?? 0;
  if (dock > 0) {
    traits.push({
      salience: 100 + dock,
      text: `docked ${dock.toFixed(dock < 10 ? 1 : 0)} at hod/lod`,
    });
  }

  const consecutive = c.v2ConsecutiveDockApplied ?? 0;
  if (consecutive > 0) {
    traits.push({
      salience: 100 + consecutive,
      text: `docked ${consecutive.toFixed(0)} for following a signal`,
    });
  }

  if (traits.length === 0) {
    return c.rejection ? c.rejection.detail : 'no measurements';
  }

  return traits
    .sort((a, b) => b.salience - a.salience)
    .slice(0, limit)
    .map((t) => t.text)
    .join(', ');
}

export interface LabelPartition {
  unlabelled: ReviewCandidate[];
  superStrong: ReviewCandidate[];
  strong: ReviewCandidate[];
  moderate: ReviewCandidate[];
  weak: ReviewCandidate[];
  notIdeal: ReviewCandidate[];
  missed: ReviewCandidate[];
}

/** Split candidates by verdict, keeping the unlabelled backlog separate. */
export function partitionByLabel(
  candidates: ReviewCandidate[],
  labels: CandidateLabelMap
): LabelPartition {
  const p: LabelPartition = {
    unlabelled: [], superStrong: [], strong: [], moderate: [], weak: [], notIdeal: [], missed: [],
  };
  for (const c of candidates) {
    const l = labels[c.id];
    if (!l) { p.unlabelled.push(c); continue; }
    switch (normaliseVerdict(l.verdict)) {
      case 'super-strong': p.superStrong.push(c); break;
      case 'strong': p.strong.push(c); break;
      case 'moderate': p.moderate.push(c); break;
      case 'weak': p.weak.push(c); break;
      case 'missed': p.missed.push(c); break;
      default: p.notIdeal.push(c);
    }
  }
  return p;
}

/**
 * How the engine's rule is doing against YOUR labels.
 *
 * Pure label arithmetic — it counts verdicts and divides. It scores nothing:
 * every candidate handed to it was already scored by the engine, and no branch
 * here can change a `score` or a `grade`. It was called `scoreRule`, which read
 * as though it were part of the scorer and got it onto the deletion list; the
 * name is the only thing about it that was ever a scorer.
 *
 * `precision` is agreement on what the rule fired on; `recall` also charges it
 * for the setups you had to add by hand. Both are only meaningful once a decent
 * number of candidates have been labelled.
 */
export function labelAgreement(
  candidates: ReviewCandidate[],
  labels: CandidateLabelMap
): {
  labelled: number;
  superStrong: number;
  strong: number;
  moderate: number;
  weak: number;
  accepted: number;
  notIdeal: number;
  missed: number;
  precisionPct: number;
  recallPct: number;
  strongPrecisionPct: number;
} {
  const p = partitionByLabel(candidates, labels);
  const superStrong = p.superStrong.length;
  const strong = p.strong.length;
  const moderate = p.moderate.length;
  const weak = p.weak.length;
  const accepted = superStrong + strong + moderate + weak;
  const notIdeal = p.notIdeal.length;
  const missed = p.missed.length;
  const fired = accepted + notIdeal;
  const shouldHaveFired = accepted + missed;
  return {
    labelled: fired + missed,
    superStrong,
    strong,
    moderate,
    weak,
    accepted,
    notIdeal,
    missed,
    // Any grade counts as a hit...
    precisionPct: fired === 0 ? 0 : (accepted / fired) * 100,
    recallPct: shouldHaveFired === 0 ? 0 : (accepted / shouldHaveFired) * 100,
    // ...but this is the number that matters if you only want to trade the best.
    // "strong" here means super-strong AND strong — the tier you'd actually trade.
    strongPrecisionPct: fired === 0 ? 0 : ((superStrong + strong) / fired) * 100,
  };
}

// ============================================================================
// Auto-tagging
// ============================================================================

/**
 * Suggest verdicts for unlabelled candidates from the ones you've labelled by
 * hand, using k-nearest-neighbours over the ENGINE's measurements.
 *
 * Deliberately derived and never persisted. Hand labels are the only ground
 * truth; if predictions were saved back they would become training data for
 * the next round and the whole thing would drift toward its own first guess.
 *
 * A suggestion is only made when the neighbours actually agree — below
 * `minConfidence` the candidate stays in the backlog rather than getting a
 * coin-flip label.
 */

export interface AutoTagOptions {
  /** Neighbours to consult. */
  k: number;
  /** Winning verdict must hold at least this share of the weighted vote (0-1). */
  minConfidence: number;
  /** Only compare a candidate against neighbours of the same direction. */
  sameDirectionOnly: boolean;
}

export const DEFAULT_AUTO_TAG_OPTIONS: AutoTagOptions = {
  k: 5,
  minConfidence: 0.6,
  sameDirectionOnly: true,
};

export interface AutoTag {
  verdict: CandidateVerdict;
  /** Reason copied from the nearest neighbour carrying the winning verdict. */
  reason: string;
  /** Weighted share of the vote the winner took, 0-1. */
  confidence: number;
  /** Id of the hand-labelled candidate this was copied from. */
  fromId: string;
}

export type AutoTagMap = Record<string, AutoTag>;

/**
 * Feature vector, entirely from engine-sent measurements.
 *
 * It used to include close strength and two trailing-bar ratios, none of which
 * are on the wire — reproducing them meant candle arithmetic on this side, and
 * every piece of that is gone. What remains is what the rule itself is written
 * in, which is the right basis for "is this bar like that one" anyway.
 *
 * Null when the bar has an incomplete set: an availability rejection (no
 * trailing bar, ATR not warm) was never measured, and treating its missing
 * numbers as zeros would place it next to a perfectly tight child at the origin.
 */
function featureVector(c: ReviewCandidate): number[] | null {
  const v = [c.atrRatio, c.parentBodyPct, c.childBodyPctOfParentBody, c.parentRange];
  return v.every((n): n is number => typeof n === 'number' && Number.isFinite(n))
    ? (v as number[])
    : null;
}

/** Min/max per feature across the pool, so no single scale dominates distance. */
function featureRanges(vectors: number[][]): { min: number[]; max: number[] } {
  const width = vectors[0]?.length ?? 0;
  const min = new Array(width).fill(Number.POSITIVE_INFINITY);
  const max = new Array(width).fill(Number.NEGATIVE_INFINITY);
  for (const v of vectors) {
    for (let i = 0; i < width; i++) {
      if (v[i] < min[i]) min[i] = v[i];
      if (v[i] > max[i]) max[i] = v[i];
    }
  }
  return { min, max };
}

function normalisedDistance(
  va: number[],
  vb: number[],
  ranges: { min: number[]; max: number[] }
): number {
  let sum = 0;
  for (let i = 0; i < va.length; i++) {
    const span = ranges.max[i] - ranges.min[i];
    // A feature that never varies carries no information; skip it rather than
    // dividing by zero.
    if (!Number.isFinite(span) || span <= 0) continue;
    const d = (va[i] - vb[i]) / span;
    sum += d * d;
  }
  return Math.sqrt(sum);
}

export function autoTagCandidates(
  candidates: ReviewCandidate[],
  labels: CandidateLabelMap,
  options: AutoTagOptions = DEFAULT_AUTO_TAG_OPTIONS
): AutoTagMap {
  const out: AutoTagMap = {};

  // Measure once. A bar with an incomplete set can neither vote nor be voted
  // on, so it drops out of both pools rather than being compared at the origin.
  const measured = candidates
    .map((c) => ({ c, v: featureVector(c) }))
    .filter((m): m is { c: ReviewCandidate; v: number[] } => m.v !== null);

  const labelled = measured.filter((m) => labels[m.c.id]);
  const unlabelled = measured.filter((m) => !labels[m.c.id]);
  if (labelled.length === 0 || unlabelled.length === 0) return out;

  const ranges = featureRanges(measured.map((m) => m.v));

  for (const target of unlabelled) {
    const pool = options.sameDirectionOnly
      ? labelled.filter((m) => m.c.direction === target.c.direction)
      : labelled;
    if (pool.length === 0) continue;

    const neighbours = pool
      .map((m) => ({ m, d: normalisedDistance(target.v, m.v, ranges) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, Math.max(1, options.k));

    // Inverse-distance weighting: an exact match shouldn't divide by zero, and
    // a far-away neighbour shouldn't count as much as a close one.
    const votes = new Map<CandidateVerdict, number>();
    let total = 0;
    for (const n of neighbours) {
      const verdict = normaliseVerdict(labels[n.m.c.id].verdict);
      const weight = 1 / (n.d + 0.05);
      votes.set(verdict, (votes.get(verdict) ?? 0) + weight);
      total += weight;
    }

    let best: CandidateVerdict | null = null;
    let bestWeight = 0;
    for (const [verdict, weight] of votes) {
      if (weight > bestWeight) {
        best = verdict;
        bestWeight = weight;
      }
    }
    if (!best || total <= 0) continue;

    const confidence = bestWeight / total;
    if (confidence < options.minConfidence) continue;

    // Copy the reason off the closest neighbour that actually voted the winner.
    const source = neighbours.find(
      (n) => normaliseVerdict(labels[n.m.c.id].verdict) === best
    );
    if (!source) continue;

    // Every auto-tag carries a reason. It used to be the reason you typed on
    // the NEIGHBOUR, or "matches your strong call at 10:32" when that was
    // blank — both of which describe a different bar. Read back a week later
    // neither tells you anything about the one you're looking at, and the
    // clock time is meaningless once the session has rolled.
    //
    // So the reason now describes THIS bar. Where the neighbour's wording adds
    // something, it rides alongside rather than standing in for it.
    const copied = (labels[source.m.c.id].reason ?? '').trim();
    const described = describeCandidate(target.c);
    out[target.c.id] = {
      verdict: best,
      reason: copied ? `${described} — like your "${copied}"` : described,
      confidence,
      fromId: source.m.c.id,
    };
  }

  return out;
}
